import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'
import {buildQueryString, normalizeParameters, stableStringify} from './ssr-query-utils'

const isSmartSearchParameters = (parameters) =>
    parameters?.smartSearch === 'true' || parameters?.smartSearch === true

const fetchProductSearchRoute = async (origin, path, parameters, fetchOptions = {}) => {
    const response = await fetch(`${origin}${path}${buildQueryString(parameters)}`, {
        method: 'GET',
        ...fetchOptions
    })

    if (!response.ok) {
        let message = 'Failed to fetch product search results'
        try {
            const errorJson = await response.json()
            if (errorJson?.error) message = errorJson.error
        } catch (error) {
            // Ignore JSON parsing errors for non-JSON responses
        }
        throw new Error(message)
    }

    return response.json()
}

const fetchProductSearch = async (origin, parameters, accessToken) => {
    const isSmartRequest = isSmartSearchParameters(parameters)
    if (!isSmartRequest) {
        return fetchProductSearchRoute(origin, '/api/product-search-ssr', parameters)
    }

    const smartFetchOptions =
        accessToken ?
            {headers: {authorization: `Bearer ${accessToken}`}}
        :   {}

    const {smartSearch: _smartFlag, ...lexicalParameters} = parameters
    const smartResult = await fetchProductSearchRoute(
        origin,
        '/api/product-search-smart',
        lexicalParameters,
        smartFetchOptions
    )

    if ((smartResult?.total ?? 0) > 0) {
        return smartResult
    }

    const lexical = await fetchProductSearchRoute(origin, '/api/product-search-ssr', lexicalParameters)
    return {...lexical, isSmartSearch: false, usedLexicalFallback: true}
}

export const useProductSearchSSR = (apiOptions = {}, queryOptions = {}) => {
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const localeId = locale?.id || locale?.alias
    const origin = useSsrFetchOrigin()
    const {getTokenWhenReady} = useAccessToken()
    const customerId = useCustomerId()

    const parameters = useMemo(
        () => {
            const merged = {
                ...apiOptions?.parameters,
                locale: apiOptions?.parameters?.locale || apiOptions?.parameters?.localeId || localeId,
                siteId: apiOptions?.parameters?.siteId || siteId
            }
            return normalizeParameters(merged)
        },
        [apiOptions?.parameters, localeId, siteId]
    )

    const isSmartRequest = isSmartSearchParameters(parameters)
    const queryKey = useMemo(
        () => [
            'product-search',
            stableStringify(parameters),
            isSmartRequest ? customerId : null
        ],
        [parameters, isSmartRequest, customerId]
    )

    return useQuery({
        ...queryOptions,
        queryKey,
        queryFn: async () => {
            const accessToken = isSmartRequest ? await getTokenWhenReady() : null
            return fetchProductSearch(origin, parameters, accessToken)
        }
    })
}

